export const MAX_DETECTION_OBJECTS = 6;
export const DEFAULT_THRESHOLD = 0.5;
export const MAX_SEARCH_BUFFER_M = 99999;
export const SEARCH_BUFFER_STEP_M = 5;

/** Default display/quality zoom shown to the user. Tiling is performed at TILE_ZOOM − tileZoomOffset. */
export const TILE_ZOOM = 21;

/** Default offset between the user-facing quality zoom and the internal OL tile grid zoom used for splitting. */
export const DEFAULT_TILE_ZOOM_OFFSET = 1;

/** Maximum number of tiles allowed for a detection run (same cap as GeoTIFF export). */
export const MAX_DETECTION_TILES = 1024;

/**
 * Default server-side overlap buffer applied to each detection tile so adjacent tiles overlap,
 * as a fraction of a representative tile's average side length in metres — converted to an
 * actual `bufferMeter` value before being sent to the API (see `DetectionService.buildRequests$`).
 */
export const DEFAULT_TILE_BUFFER_PCT = 0.4;

/** Default IoU threshold for merging fragmented detections. Pairs with IoU ≥ this value are merged. */
export const DEFAULT_IOU_THRESHOLD = 0.6;

/**
 * Default inward margin, as a fraction of each tile's real (unbuffered) extent, used to decide
 * whether a detection "crosses" its tile's edge. A detection is only treated as fully contained
 * (non-crossing) when it stays clear of this inner border band on all sides — otherwise it's a
 * cross-tile merge candidate, even if it doesn't literally touch the tile boundary.
 */
export const DEFAULT_TILE_EDGE_MARGIN_PCT = 0.05;

/**
 * Number of persistent Web Workers `MaskVectorizerService` spins up to decode pixel masks and
 * trace their contours off the main thread (see `maskVectorizerWorkerMain`). Each worker is a
 * real OS thread, so this bounds CPU/memory usage rather than throttling main-thread promise
 * concurrency — raise cautiously, each worker holds its own canvas backing store while decoding.
 */
export const MASK_VECTORIZATION_CONCURRENCY = 4;

function hslToHex(h: number, s: number, l: number): string {
  const sl = s / 100;
  const ll = l / 100;
  const a = sl * Math.min(ll, 1 - ll);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = ll - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/** Generates a random vibrant hex color for a new detection object. */
export function generateObjectColor(): string {
  const h = Math.floor(Math.random() * 360);
  const s = 65 + Math.floor(Math.random() * 25); // 65–90 %
  const l = 38 + Math.floor(Math.random() * 22); // 38–60 %
  return hslToHex(h, s, l);
}

export const CUSTOM_ELEMENTS_EXPORT_FORMATS = [
  {
    id: "geojson",
    label: "GeoJSON",
    ext: ".geojson",
    icon: "map",
    accent: "#56A7B7",
    desc: "Standard geographic data format. Compatible with most GIS tools.",
    recommended: true,
  },
  {
    id: "wkt",
    label: "WKT",
    ext: ".wkt",
    icon: "text_snippet",
    accent: "#9C27B0",
    desc: "Well-Known Text geometry format for database and API integration.",
  },
] as const;
